import React from "react";
import { Save, X, ShieldCheck, RefreshCw, Trash2, ChevronDown, ChevronUp, AlertTriangle, Info, CheckCircle2, Circle } from "lucide-react";

const fontMap = {
  xs:  { subtitle: "0.875rem", body: "0.75rem",   small: "0.6875rem", tiny: "0.625rem"  },
  sm:  { subtitle: "1rem",     body: "0.875rem",  small: "0.75rem",   tiny: "0.6875rem" },
  md:  { subtitle: "1.125rem", body: "0.9375rem", small: "0.8125rem", tiny: "0.75rem"   },
  lg:  { subtitle: "1.25rem",  body: "1rem",      small: "0.875rem",  tiny: "0.8125rem" },
  xl:  { subtitle: "1.375rem", body: "1rem",      small: "0.875rem",  tiny: "0.8125rem" },
  xxl: { subtitle: "1.5rem",   body: "1.125rem",  small: "1rem",      tiny: "0.875rem"  },
};

/* ─── Small reusable styled decision-toggle button ─── */
const DecisionBtn = ({ active, color, onClick, disabled, children, icon }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      display: "inline-flex", alignItems: "center", gap: "5px",
      padding: "5px 12px", borderRadius: "20px", fontSize: "0.78rem",
      fontWeight: active ? 700 : 500, cursor: disabled ? "not-allowed" : "pointer",
      border: `1.5px solid ${active ? color : "rgba(148,163,184,0.3)"}`,
      background: active ? `${color}18` : "transparent",
      color: active ? color : "#94a3b8",
      transition: "all 0.15s ease",
      whiteSpace: "nowrap",
    }}
  >
    {icon}
    {children}
  </button>
);

const ImportPreviewModal = ({
  importPreviewData,
  onClose,
  pendingImportFile,
  duplicateDecisions,
  setDuplicateDecisions,
  managerResolutions,
  setManagerResolutions,
  previewStep,
  setPreviewStep,
  expandedDupKeys,
  setExpandedDupKeys,
  isImportCommitting,
  isPreparingImport,
  commitImportWithDecisions,
  theme,
  language,
  screenSize,
  text,
  showAlert,
}) => {
  const fonts = fontMap[screenSize] || fontMap.lg;
  const isDark = theme === "dark";
  const isRtl = language === "ar";

  const colors = {
    bg:       isDark ? "#0f172a" : "#ffffff",
    surface:  isDark ? "#1e293b" : "#f8fafc",
    surface2: isDark ? "#162032" : "#f1f5f9",
    border:   isDark ? "rgba(148,163,184,0.15)" : "rgba(15,23,42,0.08)",
    text:     isDark ? "#f1f5f9" : "#0f172a",
    subtext:  isDark ? "#94a3b8" : "#64748b",
    muted:    isDark ? "#475569" : "#cbd5e1",
  };

  const duplicates = Array.isArray(importPreviewData?.duplicates) ? importPreviewData.duplicates : [];
  const totalDups  = duplicates.length;
  const resolved   = duplicates.filter((d) => !!duplicateDecisions[d.duplicateKey]).length;
  const progressPct = totalDups > 0 ? Math.round((resolved / totalDups) * 100) : 0;

  const hasErrOrWarn =
    importPreviewData.failed?.some((f) => Array.isArray(f.missingFields) && f.missingFields.length > 0) ||
    importPreviewData.failed?.some((f) => !Array.isArray(f.missingFields) || f.missingFields.length === 0) ||
    !!importPreviewData.parseWarnings?.length ||
    !!importPreviewData.managerWarnings?.length;
  const hasDups = totalDups > 0;

  const getManagerResolutionValue = (row) => {
    const key = String(row);
    return managerResolutions?.[row] ?? managerResolutions?.[key] ?? "";
  };

  const setManagerResolutionValue = (row, value) => {
    const key = String(row);
    setManagerResolutions((prev) => ({ ...prev, [key]: value }));
  };

  const applyDecisionToAllDuplicates = (decisionValue) => {
    if (!hasDups) return;
    const next = duplicates.reduce((acc, dup) => {
      if (dup?.duplicateKey) acc[dup.duplicateKey] = decisionValue;
      return acc;
    }, {});
    setDuplicateDecisions((prev) => ({ ...prev, ...next }));
  };

  /* ─── Step indicator ─── */
  const StepIndicator = () => {
    const steps = [
      { label: isRtl ? "الأخطاء والتحذيرات" : "Errors & Warnings", num: 1 },
      { label: isRtl ? "مراجعة المكررات" : "Duplicate Review", num: 2 },
    ];
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "0", marginBottom: "2px" }}>
        {steps.map((step, idx) => {
          const isActive   = previewStep === step.num;
          const isDone     = previewStep > step.num;
          const isDisabled = !hasErrOrWarn && step.num === 1;
          return (
            <React.Fragment key={step.num}>
              {idx > 0 && (
                <div style={{ flex: 1, height: "2px", background: isDone ? "#3b82f6" : colors.border, margin: "0 6px", minWidth: "20px" }} />
              )}
              <div style={{ display: "flex", alignItems: "center", gap: "7px", opacity: isDisabled ? 0.4 : 1 }}>
                <div style={{
                  width: "26px", height: "26px", borderRadius: "50%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: fonts.tiny, fontWeight: 700,
                  background: isActive ? "#3b82f6" : isDone ? "#10b981" : colors.surface2,
                  color: isActive || isDone ? "#fff" : colors.subtext,
                  border: isActive ? "2px solid #3b82f6" : isDone ? "2px solid #10b981" : `2px solid ${colors.border}`,
                  flexShrink: 0,
                }}>
                  {isDone ? <CheckCircle2 size={14} /> : step.num}
                </div>
                <span style={{
                  fontSize: fonts.tiny, fontWeight: isActive ? 700 : 400,
                  color: isActive ? colors.text : colors.subtext,
                  whiteSpace: "nowrap",
                }}>{step.label}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(0,0,0,0.5)",
          zIndex: 9999,
          backdropFilter: "blur(3px)",
          animation: "fadeIn 300ms ease-out",
        }}
        onClick={() => !isImportCommitting && onClose()}
      />

      {/* Modal panel */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 10000,
          width: "92%",
          maxWidth: 1200,
          maxHeight: "92vh",
          background: isDark
            ? "linear-gradient(135deg, #0f172a 0%, #1a2a3a 100%)"
            : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
          color: isDark ? "#e6eef8" : "#0f172a",
          borderRadius: 16,
          boxShadow: isDark
            ? "0 25px 60px rgba(0,0,0,0.8), 0 0 1px rgba(255,255,255,0.1)"
            : "0 20px 50px rgba(0,0,0,0.12), 0 0 1px rgba(0,0,0,0.04)",
          border: isDark
            ? "1px solid rgba(255,255,255,0.1)"
            : "1px solid rgba(148,163,184,0.15)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          direction: isRtl ? "rtl" : "ltr",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          animation: "slideUp 400ms cubic-bezier(0.34, 1.56, 0.64, 1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >

        {/* ═══ HEADER ═══ */}
        <div style={{
          padding: "32px 32px 20px",
          marginBottom: 8,
          paddingBottom: 20,
          borderBottom: isDark
            ? "1px solid rgba(255,255,255,0.08)"
            : "1px solid rgba(0,0,0,0.08)",
          flexShrink: 0,
        }}>
          {/* Close button */}
          <button
            onClick={() => !isImportCommitting && onClose()}
            disabled={isImportCommitting}
            style={{
              position: "absolute",
              top: 14,
              right: language === "ar" ? "auto" : 14,
              left: language === "ar" ? 14 : "auto",
              width: 40,
              height: 40,
              borderRadius: 10,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: isDark ? "rgba(13,18,28,0.15)" : "rgba(255,255,255,0.92)",
              border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
              cursor: isImportCommitting ? "not-allowed" : "pointer",
              transition: "all 200ms ease",
              zIndex: 20,
              color: isDark ? "#94a3b8" : "#475569",
            }}
          >
            <X size={20} />
          </button>

          {/* Title with icon */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              flexShrink: 0,
              background: previewStep === 1
                ? (isDark ? "rgba(245,158,11,0.15)" : "rgba(245,158,11,0.12)")
                : (isDark ? "rgba(59,130,246,0.15)" : "rgba(59,130,246,0.12)"),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: previewStep === 1
                ? "1px solid rgba(245,158,11,0.2)"
                : "1px solid rgba(59,130,246,0.2)",
            }}>
              {previewStep === 1
                ? <AlertTriangle size={24} color="#f59e0b" />
                : <RefreshCw size={24} color="#3b82f6" />}
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={{
                margin: 0,
                fontSize: 24,
                fontWeight: 800,
                background: isDark
                  ? "linear-gradient(135deg, #fff 0%, #cbd5e1 100%)"
                  : "linear-gradient(135deg, #0f172a 0%, #1e40af 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                letterSpacing: "-0.5px",
                lineHeight: 1.2,
              }}>
                {previewStep === 1
                  ? (isRtl ? "الأخطاء والتحذيرات" : "Errors & Warnings")
                  : (isRtl ? "مراجعة المكررات" : "Duplicate Review")}
              </h2>
              <p style={{
                margin: "4px 0 0",
                fontSize: fonts.tiny,
                color: isDark ? "#94a3b8" : "#64748b",
                lineHeight: 1.4,
              }}>
                {previewStep === 1
                  ? (isRtl ? "راجع الأخطاء أدناه. الصفوف الخاطئة سيتم تجاهلها." : "Review issues below. Rows with errors will be skipped.")
                  : (isRtl ? "بعض الموظفين موجودون بالفعل. اختر ما تريد فعله." : "Some employees already exist. Choose what to do with each.")}
              </p>
            </div>
          </div>

          {/* Step indicator */}
          {hasErrOrWarn && hasDups && <StepIndicator />}

          {/* Summary pills */}
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "14px" }}>
            {[
              { label: isRtl ? "جديد" : "New", value: importPreviewData.summary?.toCreate || 0, color: "#10b981" },
              { label: isRtl ? "تحديث" : "Update", value: importPreviewData.summary?.toUpdate || 0, color: "#3b82f6" },
              { label: isRtl ? "مكرر" : "Duplicate", value: importPreviewData.summary?.duplicates || 0, color: "#f59e0b" },
              importPreviewData.parseWarnings?.length
                ? { label: isRtl ? "تحذيرات" : "Warnings", value: importPreviewData.parseWarnings.length, color: "#8b5cf6" }
                : null,
              importPreviewData.managerWarnings?.length
                ? { label: isRtl ? "مدير غير محدد" : "Unresolved Manager", value: importPreviewData.managerWarnings.length, color: "#f97316" }
                : null,
              importPreviewData.failed?.filter((f) => Array.isArray(f.missingFields) && f.missingFields.length > 0).length
                ? { label: isRtl ? "أخطاء" : "Errors", value: importPreviewData.failed.filter((f) => Array.isArray(f.missingFields) && f.missingFields.length > 0).length, color: "#ef4444" }
                : null,
              importPreviewData.failed?.filter((f) => !Array.isArray(f.missingFields) || f.missingFields.length === 0).length
                ? { label: isRtl ? "فشل" : "Failed", value: importPreviewData.failed.filter((f) => !Array.isArray(f.missingFields) || f.missingFields.length === 0).length, color: "#ef4444" }
                : null,
            ].filter(Boolean).map((pill, i) => (
              <div key={i} style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: fonts.tiny,
                background: `${pill.color}14`,
                border: `1px solid ${pill.color}30`,
              }}>
                <span style={{ fontWeight: 800, color: pill.color }}>{pill.value}</span>
                <span style={{ color: isDark ? "#94a3b8" : "#64748b", fontWeight: 600 }}>{pill.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ═══ BODY ═══ */}
        <div style={{ flex: 1, overflowY: "auto", scrollbarWidth: "thin", scrollbarColor: `${colors.muted} transparent` }}>

          {/* ─── PAGE 1: Errors & Warnings ─── */}
          {previewStep === 1 && (
            <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: "16px" }}>

              {/* Validation errors */}
              {(() => {
                const errs = (importPreviewData.failed || []).filter(
                  (f) => Array.isArray(f.missingFields) && f.missingFields.length > 0
                );
                if (!errs.length) return null;
                return (
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                      <AlertTriangle size={15} color="#f59e0b" />
                      <span style={{ fontSize: fonts.small, fontWeight: 700, color: "#f59e0b" }}>
                        {isRtl ? `أخطاء الحقول المطلوبة (${errs.length})` : `Required Field Errors (${errs.length})`}
                      </span>
                    </div>
                    <p style={{ margin: "0 0 8px", fontSize: fonts.tiny, color: colors.subtext }}>
                      {isRtl
                        ? "هذه الصفوف تفتقر إلى حقول إلزامية وستُتخطى تلقائيًا."
                        : "These rows are missing required fields and will be skipped automatically."}
                    </p>
                    <div style={{ borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(245,158,11,0.25)" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ background: isDark ? "rgba(245,158,11,0.08)" : "rgba(245,158,11,0.05)" }}>
                            <th style={{ padding: "9px 14px", fontSize: fonts.tiny, fontWeight: 600, color: "#f59e0b", textAlign: isRtl ? "right" : "left", width: "70px" }}>{isRtl ? "الصف" : "Row"}</th>
                            <th style={{ padding: "9px 14px", fontSize: fonts.tiny, fontWeight: 600, color: "#f59e0b", textAlign: isRtl ? "right" : "left" }}>{isRtl ? "الحقول المفقودة" : "Missing Fields"}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {errs.map((f, i) => (
                            <tr key={i} style={{ background: i % 2 === 0 ? "transparent" : (isDark ? "rgba(255,255,255,0.015)" : "rgba(0,0,0,0.015)") }}>
                              <td style={{ padding: "8px 14px", fontSize: fonts.small, color: "#f59e0b", fontWeight: 700, borderTop: `1px solid ${colors.border}` }}>{f.row}</td>
                              <td style={{ padding: "8px 14px", fontSize: fonts.small, borderTop: `1px solid ${colors.border}` }}>
                                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                                  {f.missingFields.map((field, fi) => (
                                    <span key={fi} style={{ padding: "2px 8px", borderRadius: "6px", fontSize: fonts.tiny, fontWeight: 600, background: "rgba(239,68,68,0.1)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.2)" }}>{field}</span>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* Other failures (e.g. quota exceeded), no missingFields, just a reason */}
              {(() => {
                const otherFailed = (importPreviewData.failed || []).filter(
                  (f) => !Array.isArray(f.missingFields) || f.missingFields.length === 0
                );
                if (!otherFailed.length) return null;
                return (
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                      <AlertTriangle size={15} color="#ef4444" />
                      <span style={{ fontSize: fonts.small, fontWeight: 700, color: "#ef4444" }}>
                        {isRtl ? `صفوف فشلت (${otherFailed.length})` : `Rows That Will Fail (${otherFailed.length})`}
                      </span>
                    </div>
                    <p style={{ margin: "0 0 8px", fontSize: fonts.tiny, color: colors.subtext }}>
                      {isRtl
                        ? "هذه الصفوف, مثل تجاوز حصة الموظفين أو الحسابات, سيتم تخطيها."
                        : "These rows, e.g. employee/account quota exceeded, will be skipped."}
                    </p>
                    <div style={{ borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(239,68,68,0.25)" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ background: isDark ? "rgba(239,68,68,0.08)" : "rgba(239,68,68,0.05)" }}>
                            <th style={{ padding: "9px 14px", fontSize: fonts.tiny, fontWeight: 600, color: "#ef4444", textAlign: isRtl ? "right" : "left", width: "70px" }}>{isRtl ? "الصف" : "Row"}</th>
                            <th style={{ padding: "9px 14px", fontSize: fonts.tiny, fontWeight: 600, color: "#ef4444", textAlign: isRtl ? "right" : "left" }}>{isRtl ? "السبب" : "Reason"}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {otherFailed.map((f, i) => (
                            <tr key={i} style={{ background: i % 2 === 0 ? "transparent" : (isDark ? "rgba(255,255,255,0.015)" : "rgba(0,0,0,0.015)") }}>
                              <td style={{ padding: "8px 14px", fontSize: fonts.small, color: "#ef4444", fontWeight: 700, borderTop: `1px solid ${colors.border}` }}>{f.row}</td>
                              <td style={{ padding: "8px 14px", fontSize: fonts.small, color: colors.text, borderTop: `1px solid ${colors.border}` }}>{f.reason || f.error || "Unknown error"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* Parse warnings */}
              {(() => {
                const warns = importPreviewData.parseWarnings || [];
                if (!warns.length) return null;
                return (
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                      <Info size={15} color="#8b5cf6" />
                      <span style={{ fontSize: fonts.small, fontWeight: 700, color: "#8b5cf6" }}>
                        {isRtl ? `تحذيرات التحليل (${warns.length})` : `Parse Warnings (${warns.length})`}
                      </span>
                    </div>
                    <p style={{ margin: "0 0 8px", fontSize: fonts.tiny, color: colors.subtext }}>
                      {isRtl
                        ? "هذه الصفوف استُوردت، لكن بعض القيم لم يمكن تحويلها وحُفظت فارغة."
                        : "These rows will be imported, but some values couldn't be converted and will be saved as empty."}
                    </p>
                    <div style={{ borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(139,92,246,0.25)" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ background: isDark ? "rgba(139,92,246,0.08)" : "rgba(139,92,246,0.05)" }}>
                            {[isRtl ? "الصف" : "Row", isRtl ? "الحقل" : "Field", isRtl ? "القيمة" : "Value", isRtl ? "النوع المتوقع" : "Expected"].map((h, i) => (
                              <th key={i} style={{ padding: "9px 14px", fontSize: fonts.tiny, fontWeight: 600, color: "#8b5cf6", textAlign: isRtl ? "right" : "left" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {warns.flatMap((w, wi) =>
                            w.parseErrors.map((e, ei) => (
                              <tr key={`${wi}-${ei}`} style={{ background: wi % 2 === 0 ? "transparent" : (isDark ? "rgba(255,255,255,0.015)" : "rgba(0,0,0,0.015)") }}>
                                {ei === 0 && <td style={{ padding: "8px 14px", fontSize: fonts.small, fontWeight: 700, color: "#8b5cf6", borderTop: `1px solid ${colors.border}` }} rowSpan={w.parseErrors.length}>{w.row}</td>}
                                <td style={{ padding: "8px 14px", fontSize: fonts.small, fontWeight: 600, color: colors.text, borderTop: `1px solid ${colors.border}` }}>{e.field}</td>
                                <td style={{ padding: "8px 14px", fontSize: fonts.small, color: "#ef4444", borderTop: `1px solid ${colors.border}` }}>{e.value}</td>
                                <td style={{ padding: "8px 14px", fontSize: fonts.small, color: colors.subtext, borderTop: `1px solid ${colors.border}` }}>{e.expectedType}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* Manager warnings, row imported, but its "First Line
                  Manager Client Code" / Name couldn't be matched to exactly
                  one employee (not found, or more than one candidate).
                  Non-blocking: the employee still gets created/updated,
                  just without that manager link until fixed afterward via
                  Edit Employee → First Line Manager. */}
              {(() => {
                const mgrWarns = importPreviewData.managerWarnings || [];
                if (!mgrWarns.length) return null;
                return (
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                      <AlertTriangle size={15} color="#f97316" />
                      <span style={{ fontSize: fonts.small, fontWeight: 700, color: "#f97316" }}>
                        {isRtl ? `تحذيرات المدير المباشر (${mgrWarns.length})` : `First Line Manager Warnings (${mgrWarns.length})`}
                      </span>
                    </div>
                    <p style={{ margin: "0 0 8px", fontSize: fonts.tiny, color: colors.subtext }}>
                      {isRtl
                        ? "سيتم استيراد هذه الصفوف، لكن لم يتم العثور على المدير المباشر المطابق أو أنه غير واضح. يمكن تصحيح ذلك لاحقًا من تعديل الموظف."
                        : "These rows will still be imported, but their First Line Manager couldn't be matched (not found, or matches more than one employee). Fix afterward via Edit Employee."}
                    </p>
                    <div style={{ borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(249,115,22,0.25)" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ background: isDark ? "rgba(249,115,22,0.08)" : "rgba(249,115,22,0.05)" }}>
                            {[isRtl ? "الصف" : "Row", isRtl ? "القيمة المُدخلة" : "Entered Value", isRtl ? "السبب" : "Reason", isRtl ? "حل المدير" : "Resolve Manager"].map((h, i) => (
                              <th key={i} style={{ padding: "9px 14px", fontSize: fonts.tiny, fontWeight: 600, color: "#f97316", textAlign: isRtl ? "right" : "left" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {mgrWarns.map((w, i) => {
                            const resolutionValue = getManagerResolutionValue(w.row);
                            return (
                              <tr key={i} style={{ background: i % 2 === 0 ? "transparent" : (isDark ? "rgba(255,255,255,0.015)" : "rgba(0,0,0,0.015)") }}>
                                <td style={{ padding: "8px 14px", fontSize: fonts.small, fontWeight: 700, color: "#f97316", borderTop: `1px solid ${colors.border}` }}>{w.row}</td>
                                <td style={{ padding: "8px 14px", fontSize: fonts.small, color: colors.text, borderTop: `1px solid ${colors.border}` }}>{w.rawValue || "—"}</td>
                                <td style={{ padding: "8px 14px", fontSize: fonts.small, color: colors.subtext, borderTop: `1px solid ${colors.border}` }}>
                                  {w.reason === "ambiguous"
                                    ? (isRtl ? `مطابقة لأكثر من موظف (${w.candidateCount})` : `Matches ${w.candidateCount} employees, too ambiguous to auto-assign`)
                                    : w.reason === "missing"
                                      ? (isRtl ? "لم يتم تقديم معلومات المدير" : "No manager information was provided")
                                      : (isRtl ? "لم يتم العثور على موظف مطابق" : "No matching employee found")}
                                </td>
                                <td style={{ padding: "8px 14px", borderTop: `1px solid ${colors.border}` }}>
                                  <input
                                    type="text"
                                    value={resolutionValue}
                                    onChange={(e) => setManagerResolutionValue(w.row, e.target.value)}
                                    placeholder={isRtl ? "كود أو اسم المدير" : "Manager code or name"}
                                    style={{
                                      width: "100%",
                                      padding: "8px 10px",
                                      borderRadius: 8,
                                      border: `1px solid ${colors.border}`,
                                      background: isDark ? "rgba(255,255,255,0.04)" : "#fff",
                                      color: colors.text,
                                      fontSize: fonts.small,
                                    }}
                                  />
                                  {resolutionValue && (
                                    <div style={{ marginTop: 6, fontSize: fonts.tiny, color: "#10b981" }}>
                                      {isRtl ? "سيُستخدم هذا المدير عند التأكيد." : "This manager will be used on commit."}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* All clear */}
              {!hasErrOrWarn && (
                <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "16px", borderRadius: "12px", background: "rgba(16,185,129,0.07)", border: "1px solid rgba(16,185,129,0.2)" }}>
                  <CheckCircle2 size={18} color="#10b981" />
                  <span style={{ fontSize: fonts.small, color: "#10b981", fontWeight: 600 }}>
                    {isRtl ? "لا توجد أخطاء أو تحذيرات." : "No errors or warnings found."}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* ─── PAGE 2: Duplicate Review ─── */}
          {previewStep === 2 && (
            <div style={{ display: "flex", flexDirection: "column" }}>

              {/* Progress + Bulk actions bar */}
              <div style={{
                padding: "14px 24px",
                borderBottom: `1px solid ${colors.border}`,
                background: isDark ? "rgba(15,23,42,0.4)" : "rgba(248,250,252,0.9)",
                display: "flex", flexDirection: "column", gap: "12px",
                flexShrink: 0,
              }}>
                {/* Progress bar */}
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                    <span style={{ fontSize: fonts.tiny, fontWeight: 600, color: colors.subtext }}>
                      {isRtl ? "تقدم المراجعة" : "Review Progress"}
                    </span>
                    <span style={{ fontSize: fonts.tiny, fontWeight: 700, color: progressPct === 100 ? "#10b981" : colors.text }}>
                      {resolved} / {totalDups} {isRtl ? "تم" : "resolved"}
                      {progressPct === 100 && " ✓"}
                    </span>
                  </div>
                  <div style={{ height: "6px", borderRadius: "99px", background: isDark ? "rgba(148,163,184,0.15)" : "rgba(15,23,42,0.08)", overflow: "hidden" }}>
                    <div style={{
                      height: "100%", borderRadius: "99px",
                      width: `${progressPct}%`,
                      background: progressPct === 100 ? "#10b981" : "linear-gradient(90deg, #3b82f6, #6366f1)",
                      transition: "width 0.3s ease",
                    }} />
                  </div>
                </div>

                {/* Bulk actions */}
                <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                  <span style={{ fontSize: fonts.tiny, color: colors.subtext, fontWeight: 600, whiteSpace: "nowrap" }}>
                    {isRtl ? "تطبيق على الكل:" : "Select all:"}
                  </span>
                  <button
                    onClick={() => applyDecisionToAllDuplicates("keepExisting")}
                    disabled={isImportCommitting || isPreparingImport}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: "5px",
                      padding: "5px 12px", borderRadius: "8px", fontSize: fonts.tiny,
                      fontWeight: 600, cursor: "pointer", border: "1.5px solid rgba(107,114,128,0.4)",
                      background: isDark ? "rgba(107,114,128,0.12)" : "rgba(107,114,128,0.08)",
                      color: isDark ? "#94a3b8" : "#374151",
                    }}
                  >
                    <ShieldCheck size={13} />
                    {isRtl ? "الإبقاء على الكل" : "Keep All Existing"}
                  </button>
                  <button
                    onClick={() => applyDecisionToAllDuplicates("useIncoming")}
                    disabled={isImportCommitting || isPreparingImport}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: "5px",
                      padding: "5px 12px", borderRadius: "8px", fontSize: fonts.tiny,
                      fontWeight: 600, cursor: "pointer", border: "1.5px solid rgba(16,185,129,0.35)",
                      background: "rgba(16,185,129,0.08)", color: "#10b981",
                    }}
                  >
                    <RefreshCw size={13} />
                    {isRtl ? "تحديث الكل من الإكسل" : "Update All from Excel"}
                  </button>
                  <button
                    onClick={() => setDuplicateDecisions({})}
                    disabled={isImportCommitting || isPreparingImport}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: "5px",
                      padding: "5px 12px", borderRadius: "8px", fontSize: fonts.tiny,
                      fontWeight: 500, cursor: "pointer", border: `1.5px solid ${colors.border}`,
                      background: "transparent", color: colors.muted,
                    }}
                  >
                    <Trash2 size={12} />
                    {isRtl ? "مسح الكل" : "Clear All"}
                  </button>

                  {/* Legend */}
                  <div style={{ marginInlineStart: "auto", display: "flex", gap: "10px", alignItems: "center" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: fonts.tiny, color: colors.muted }}>
                      <Circle size={10} color="#f59e0b" fill="#f59e0b" /> {isRtl ? "لم يُحدد" : "Undecided"}
                    </span>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: fonts.tiny, color: colors.muted }}>
                      <ShieldCheck size={11} color="#6b7280" /> {isRtl ? "إبقاء" : "Keep"}
                    </span>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: fonts.tiny, color: colors.muted }}>
                      <RefreshCw size={11} color="#10b981" /> {isRtl ? "تحديث" : "Update"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Duplicate rows */}
              <div style={{ overflowY: "auto", maxHeight: "calc(90vh - 330px)", scrollbarWidth: "thin", scrollbarColor: `${colors.muted} transparent` }}>
                {!hasDups ? (
                  <div style={{ padding: "24px", textAlign: "center", color: colors.subtext, fontSize: fonts.small }}>
                    {isRtl ? "لا توجد مكررات." : "No duplicates found."}
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
                    {duplicates.map((dup, idx) => {
                      const key      = dup.duplicateKey;
                      const decision = duplicateDecisions[key] || "";
                      const diffs    = Array.isArray(dup.fieldDiffs) ? dup.fieldDiffs : [];
                      const isExpanded = !!expandedDupKeys[key];

                      const rowAccent =
                        decision === "keepExisting" ? "#6b7280"
                        : decision === "useIncoming" || decision === "update" ? "#10b981"
                        : "#f59e0b";

                      const rowBg =
                        decision === "keepExisting"
                          ? (isDark ? "rgba(107,114,128,0.06)" : "rgba(107,114,128,0.04)")
                          : decision === "useIncoming" || decision === "update"
                            ? (isDark ? "rgba(16,185,129,0.06)" : "rgba(16,185,129,0.04)")
                            : (isDark ? "rgba(245,158,11,0.04)" : "rgba(245,158,11,0.03)");

                      return (
                        <div
                          key={`dup-${key}-${dup.row}`}
                          style={{
                            borderBottom: `1px solid ${colors.border}`,
                            background: rowBg,
                            borderInlineStart: `3px solid ${rowAccent}`,
                            transition: "background 0.2s",
                          }}
                        >
                          {/* Main row */}
                          <div style={{ display: "grid", gridTemplateColumns: "52px minmax(0, 1fr) minmax(0, 1fr) auto", gap: "0", alignItems: "stretch" }}>

                            {/* Row number */}
                            <div style={{
                              display: "flex", alignItems: "center", justifyContent: "center",
                              borderInlineEnd: `1px solid ${colors.border}`, padding: "14px 8px",
                              fontSize: fonts.tiny, fontWeight: 700, color: rowAccent,
                            }}>
                              {dup.row}
                            </div>

                            {/* Existing employee */}
                            <div style={{ padding: "12px 16px", borderInlineEnd: `1px solid ${colors.border}` }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
                                <span style={{
                                  fontSize: "0.65rem", fontWeight: 700, padding: "1px 6px", borderRadius: "4px",
                                  background: isDark ? "rgba(107,114,128,0.2)" : "rgba(107,114,128,0.12)",
                                  color: isDark ? "#94a3b8" : "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em",
                                }}>
                                  {isRtl ? "في النظام" : "In System"}
                                </span>
                              </div>
                              <div style={{ fontSize: fonts.small, fontWeight: 600, color: colors.text }}>{dup.existing?.sr || "—"}</div>
                              <div style={{ fontSize: fonts.tiny, color: colors.subtext }}>{dup.existing?.email || "—"}</div>
                              <div style={{ fontSize: fonts.tiny, color: colors.subtext }}>
                                {isRtl ? "كود العميل: " : "Client Code: "}{dup.existing?.clientCode || "—"}
                              </div>
                            </div>

                            {/* Incoming from Excel */}
                            <div style={{ padding: "12px 16px", borderInlineEnd: `1px solid ${colors.border}` }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
                                <span style={{
                                  fontSize: "0.65rem", fontWeight: 700, padding: "1px 6px", borderRadius: "4px",
                                  background: "rgba(59,130,246,0.1)", color: "#3b82f6",
                                  textTransform: "uppercase", letterSpacing: "0.04em",
                                }}>
                                  {isRtl ? "من الإكسل" : "From Excel"}
                                </span>
                              </div>
                              <div style={{ fontSize: fonts.small, fontWeight: 600, color: colors.text }}>{dup.incoming?.sr || "—"}</div>
                              <div style={{ fontSize: fonts.tiny, color: colors.subtext }}>{dup.incoming?.email || "—"}</div>
                              <div style={{ fontSize: fonts.tiny, color: colors.subtext }}>
                                {isRtl ? "كود العميل: " : "Client Code: "}{dup.incoming?.clientCode || "—"}
                              </div>
                            </div>

                            {/* Decision area */}
                            <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", justifyContent: "center", gap: "6px", minWidth: "200px" }}>
                              {/* Status badge */}
                              <div style={{ marginBottom: "4px" }}>
                                {!decision && (
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "0.65rem", fontWeight: 700, color: "#f59e0b", background: "rgba(245,158,11,0.1)", padding: "1px 6px", borderRadius: "4px" }}>
                                    <Circle size={8} fill="#f59e0b" /> {isRtl ? "لم يُحدد بعد" : "Needs decision"}
                                  </span>
                                )}
                                {decision === "keepExisting" && (
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "0.65rem", fontWeight: 700, color: "#6b7280", background: isDark ? "rgba(107,114,128,0.15)" : "rgba(107,114,128,0.1)", padding: "1px 6px", borderRadius: "4px" }}>
                                    <ShieldCheck size={10} /> {isRtl ? "إبقاء" : "Keep existing"}
                                  </span>
                                )}
                                {(decision === "useIncoming" || decision === "update") && (
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "0.65rem", fontWeight: 700, color: "#10b981", background: "rgba(16,185,129,0.1)", padding: "1px 6px", borderRadius: "4px" }}>
                                    <RefreshCw size={10} /> {isRtl ? "تحديث" : "Will update"}
                                  </span>
                                )}
                              </div>
                              {/* Toggle buttons */}
                              <div style={{ display: "flex", gap: "6px" }}>
                                <DecisionBtn
                                  active={decision === "keepExisting"}
                                  color="#6b7280"
                                  onClick={() => setDuplicateDecisions((prev) => ({ ...prev, [key]: "keepExisting" }))}
                                  disabled={isImportCommitting}
                                  icon={<ShieldCheck size={12} />}
                                >
                                  {isRtl ? "إبقاء" : "Keep"}
                                </DecisionBtn>
                                <DecisionBtn
                                  active={decision === "useIncoming" || decision === "update"}
                                  color="#10b981"
                                  onClick={() => setDuplicateDecisions((prev) => ({ ...prev, [key]: "useIncoming" }))}
                                  disabled={isImportCommitting}
                                  icon={<RefreshCw size={12} />}
                                >
                                  {isRtl ? "تحديث" : "Update"}
                                </DecisionBtn>
                              </div>

                              {/* Changed fields toggle */}
                              {diffs.length > 0 && (
                                <button
                                  onClick={() => setExpandedDupKeys((prev) => ({ ...prev, [key]: !prev[key] }))}
                                  style={{
                                    display: "inline-flex", alignItems: "center", gap: "4px",
                                    background: "none", border: "none", cursor: "pointer",
                                    color: isExpanded ? "#f59e0b" : "#3b82f6",
                                    fontSize: fonts.tiny, fontWeight: 600, padding: "0",
                                  }}
                                >
                                  {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                  {isExpanded
                                    ? (isRtl ? "إخفاء الفروقات" : "Hide differences")
                                    : (isRtl ? `${diffs.length} حقل متغيّر` : `${diffs.length} field${diffs.length > 1 ? "s" : ""} changed`)}
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Field diff panel */}
                          {isExpanded && diffs.length > 0 && (
                            <div style={{
                              borderTop: `1px solid ${colors.border}`,
                              padding: "12px 16px 12px 68px",
                              background: isDark ? "rgba(59,130,246,0.04)" : "rgba(59,130,246,0.025)",
                            }}>
                              <div style={{ fontSize: fonts.tiny, fontWeight: 700, color: "#3b82f6", marginBottom: "8px" }}>
                                {isRtl ? "الحقول المختلفة:" : "Changed fields:"}
                              </div>
                              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "0", borderRadius: "10px", overflow: "hidden", border: `1px solid ${colors.border}` }}>
                                {/* Header */}
                                {[isRtl ? "الحقل" : "Field", isRtl ? "الحالي بالنظام" : "Current in System", isRtl ? "القادم من الإكسل" : "From Excel"].map((h, i) => (
                                  <div key={i} style={{
                                    padding: "7px 12px", fontSize: fonts.tiny, fontWeight: 700,
                                    background: isDark ? "#1e293b" : "#f1f5f9",
                                    color: i === 0 ? "#3b82f6" : i === 1 ? "#ef4444" : "#10b981",
                                    borderBottom: `1px solid ${colors.border}`,
                                    borderInlineEnd: i < 2 ? `1px solid ${colors.border}` : "none",
                                  }}>{h}</div>
                                ))}
                                {/* Data rows */}
                                {diffs.map((d, di) => (
                                  <React.Fragment key={di}>
                                    <div style={{ padding: "7px 12px", fontSize: fonts.tiny, fontWeight: 600, color: colors.text, background: di % 2 === 0 ? "transparent" : (isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)"), borderTop: `1px solid ${colors.border}`, borderInlineEnd: `1px solid ${colors.border}` }}>{d.field}</div>
                                    <div style={{ padding: "7px 12px", fontSize: fonts.tiny, color: "#ef4444",  background: di % 2 === 0 ? "transparent" : (isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)"), borderTop: `1px solid ${colors.border}`, borderInlineEnd: `1px solid ${colors.border}` }}>{d.existing ?? <em style={{ color: colors.muted }}>—</em>}</div>
                                    <div style={{ padding: "7px 12px", fontSize: fonts.tiny, color: "#10b981", background: di % 2 === 0 ? "transparent" : (isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)"), borderTop: `1px solid ${colors.border}` }}>{d.incoming ?? <em style={{ color: colors.muted }}>—</em>}</div>
                                  </React.Fragment>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ═══ FOOTER ═══ */}
        <div style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 12,
          marginTop: "auto",
          paddingTop: 20,
          padding: "20px 32px",
          borderTop: isDark
            ? "1px solid rgba(255,255,255,0.08)"
            : "1px solid rgba(0,0,0,0.08)",
          flexShrink: 0,
        }}>

          {/* Page 1 → Next (if duplicates exist) */}
          {previewStep === 1 && hasDups && (
            <button
              onClick={() => setPreviewStep(2)}
              disabled={isImportCommitting || isPreparingImport}
              style={{
                background: " #3b82f6 ",
                color: "#fff",
                padding: "12px 24px",
                borderRadius: 10,
                border: "none",
                display: "inline-flex",
                gap: 8,
                alignItems: "center",
                cursor: "pointer",
                fontWeight: 700,
                fontSize: 14,
                transition: "all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                opacity: (isImportCommitting || isPreparingImport) ? 0.5 : 1,
              }}
            >
              {isRtl ? "التالي: مراجعة المكررات" : "Next: Review Duplicates"}
            </button>
          )}

          {/* Page 1 → Proceed directly (no duplicates) */}
          {previewStep === 1 && !hasDups && (
            <button
              onClick={async () => {
                if (!pendingImportFile) {
                  await showAlert({ message: text[language].importFailed });
                  return;
                }
                await commitImportWithDecisions(pendingImportFile, {}, managerResolutions);
              }}
              disabled={isImportCommitting || isPreparingImport}
              style={{
                background: "#2e7cba",
                color: "#fff",
                padding: "12px 24px",
                borderRadius: 10,
                border: "none",
                display: "inline-flex",
                gap: 8,
                alignItems: "center",
                cursor: "pointer",
                fontWeight: 700,
                fontSize: 14,
                transition: "all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                opacity: (isImportCommitting || isPreparingImport) ? 0.5 : 1,
              }}
            >
              <Save size={16} />
              {isImportCommitting
                ? (isRtl ? "جاري التنفيذ..." : "Processing...")
                : (isRtl ? "متابعة الاستيراد" : "Proceed Import")}
            </button>
          )}

          {/* Page 2 → Back */}
          {previewStep === 2 && hasErrOrWarn && (
            <button
              onClick={() => setPreviewStep(1)}
              disabled={isImportCommitting}
              style={{
                background: isDark ? "rgba(30,41,59,0.8)" : "rgba(241,245,249,0.9)",
                color: isDark ? "#cbd5e1" : "#475569",
                padding: "12px 24px",
                borderRadius: 10,
                border: isDark ? "1.5px solid rgba(255,255,255,0.15)" : "1.5px solid rgba(0,0,0,0.12)",
                cursor: "pointer",
                fontWeight: 700,
                fontSize: 14,
                transition: "all 300ms ease",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                opacity: isImportCommitting ? 0.5 : 1,
              }}
            >
              {isRtl ? "رجوع" : "Back"}
            </button>
          )}

          {/* Page 2 → Proceed */}
          {previewStep === 2 && (
            <button
              onClick={async () => {
                const unresolved = duplicates.filter((dup) => !duplicateDecisions[dup.duplicateKey]);
                if (unresolved.length > 0) {
                  await showAlert({
                    title: isRtl ? "مراجعة مطلوبة" : "Decision Required",
                    message: isRtl
                      ? `يوجد ${unresolved.length} صف بدون اختيار. يرجى تحديد الإجراء لكل مكرر.`
                      : `${unresolved.length} row${unresolved.length > 1 ? "s" : ""} still need a decision. Please choose Keep or Update for each duplicate.`,
                    variant: "error",
                  });
                  return;
                }
                if (!pendingImportFile) {
                  await showAlert({ message: text[language].importFailed });
                  return;
                }
                await commitImportWithDecisions(pendingImportFile, duplicateDecisions, managerResolutions);
              }}
              disabled={isImportCommitting || isPreparingImport}
              style={{
                background: "#2e7cba",
                color: "#fff",
                padding: "12px 24px",
                borderRadius: 10,
                border: "none",
                display: "inline-flex",
                gap: 8,
                alignItems: "center",
                cursor: "pointer",
                fontWeight: 700,
                fontSize: 14,
                transition: "all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                opacity: (isImportCommitting || isPreparingImport) ? 0.5 : 1,
              }}
            >
              {!isImportCommitting && <Save size={16} />}
              {isImportCommitting
                ? (isRtl ? "جاري التنفيذ..." : "Processing...")
                : (isRtl ? "تأكيد الاستيراد" : "Confirm Import")}
            </button>
          )}

          {/* Cancel */}
          <button
            onClick={onClose}
            disabled={isImportCommitting}
            style={{
              background: isDark ? "rgba(30,41,59,0.8)" : "rgba(241,245,249,0.9)",
              color: isDark ? "#cbd5e1" : "#475569",
              padding: "12px 24px",
              borderRadius: 10,
              border: isDark ? "1.5px solid rgba(255,255,255,0.15)" : "1.5px solid rgba(0,0,0,0.12)",
              cursor: isImportCommitting ? "not-allowed" : "pointer",
              fontWeight: 700,
              fontSize: 14,
              transition: "all 300ms ease",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              opacity: isImportCommitting ? 0.5 : 1,
            }}
          >
            {isRtl ? "إلغاء" : "Cancel"}
          </button>
        </div>
      </div>

      {/* Modal Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) translateY(30px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default ImportPreviewModal;
